import type { QueryClient } from "@tanstack/react-query";

import { ApiError, api } from "../../api/client";
import type { Exercise, Zone } from "../../api/types";
import { genericEnemyLabel, isNeutral } from "../../enemyDisplay";
import { unitDesignation } from "../../i18n/designation";
import { locale, t } from "../../i18n/i18n";
import { missionName } from "../../i18n/vocabulary";
import { rotationCode, rotationName } from "../../centacRotation";
import { levelLabel, missionLabel } from "../../missions";
import { profileName } from "../../views/enemyProfile";

/** Présentation d'un dossier sur l'accueil : dates, état des ordres, recherche ; import d'un fichier .generex. */

/** Texte comparé sans casse ni accents. */
export function fold(text: string): string {
  return text.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
}

/** Format de date dans la langue de l'interface (créé à l'appel : la langue peut changer). */
function format(date: Date, options: Intl.DateTimeFormatOptions): string {
  return new Intl.DateTimeFormat(locale(), options).format(date);
}

function startOfDay(date: Date): number {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
}

/** « à l'instant », « il y a 12 min », « aujourd'hui à 09:41 », « hier à 18:04 », « lundi à 10:15 »,
 *  « le 12 septembre 2026 ». */
export function formatActivity(iso: string, now = new Date()): string {
  const date = new Date(iso);
  const minutes = Math.floor((now.getTime() - date.getTime()) / 60_000);
  if (minutes < 1) return t("à l'instant");
  if (minutes < 60) return t("il y a {n} min", { n: minutes });
  const days = Math.round((startOfDay(now) - startOfDay(date)) / 86_400_000);
  const time = format(date, { hour: "2-digit", minute: "2-digit" });
  if (days <= 0) return t("aujourd'hui à {time}", { time });
  if (days === 1) return t("hier à {time}", { time });
  if (days < 7) return t("{weekday} à {time}", { weekday: format(date, { weekday: "long" }), time });
  return t("le {date}", { date: format(date, { day: "numeric", month: "long", year: "numeric" }) });
}

/** Date complète, pour l'infobulle (« lundi 29 septembre 2026 à 09:41 »). */
export function formatFullDate(iso: string): string {
  return format(new Date(iso), { dateStyle: "full", timeStyle: "short" });
}

/** Dernière activité : dossier ou l'un de ses ordres. */
export function activityOf(ex: Exercise): string {
  return ex.last_activity ?? ex.updated_at;
}

/** Dossier renseigné (camp et niveau choisis) : sa carte et son ordre existent. */
export function isComplete(ex: Exercise): boolean {
  return Boolean(ex.map_slug && ex.parameters.level);
}

/** État des ordres : « Aucun ordre », « OPO en rédaction », « OPO finalisé », « 3 OPO, dont 1 finalisé ». */
export function ordersState(ex: Exercise): { label: string; tone: "neutral" | "ok" | "muted" } {
  const count = ex.orders?.count ?? 0;
  const finalized = ex.orders?.finalized ?? 0;
  if (count === 0) return { label: t("Aucun ordre rédigé"), tone: "muted" };
  if (count === 1) {
    return finalized ? { label: t("OPO validé"), tone: "ok" } : { label: t("OPO en rédaction"), tone: "neutral" };
  }
  if (finalized === count) return { label: t("{n} OPO validés", { n: count }), tone: "ok" };
  if (finalized === 0) return { label: t("{n} OPO en rédaction", { n: count }), tone: "neutral" };
  const label =
    finalized > 1
      ? t("{n} OPO, dont {f} validés", { n: count, f: finalized })
      : t("{n} OPO, dont {f} validé", { n: count, f: finalized });
  return { label, tone: "neutral" };
}

/** Lien de chaîne d'un dossier (posé par le serveur : transmission, déclinaison, code) : dossier de l'échelon qui lui
 *  a donné son ordre. */
function chainFrom(ex: Exercise): { from: string | null; name: string } | null {
  const chain = ex.parameters.extra?.chain;
  if (!chain || typeof chain !== "object") return null;
  const link = chain as { from?: unknown; from_name?: unknown };
  return { from: typeof link.from === "string" ? link.from : null, name: typeof link.from_name === "string" ? link.from_name : "" };
}

/** Relation d'un dossier dans la bibliothèque (docs/20 §13) : l'échelon supérieur qui lui a donné son ordre (son nom
 *  actuel s'il est dans la liste, sinon celui de la transmission) et le nombre de dossiers subordonnés qu'il a
 *  produits. */
export function chainRelation(ex: Exercise, all: readonly Exercise[]): { parent: string | null; children: number } {
  const link = chainFrom(ex);
  const parentRow = link?.from ? all.find((e) => e.id === link.from) : undefined;
  const parent = link ? parentRow?.name ?? (link.name || null) : null;
  const children = all.filter((e) => e.id !== ex.id && chainFrom(e)?.from === ex.id).length;
  return { parent, children };
}

/** Dossier subordonné (créé depuis un dossier N : génération des ordres, transmission) : jamais listé dans « Tous
 *  les dossiers » (consigne d'Alexis du 08/10/2026) ; on l'ouvre depuis son dossier d'origine (« Ordres
 *  subordonnés »). */
export function isSubordinate(ex: Exercise, _all?: readonly Exercise[]): boolean {
  const from = chainFrom(ex)?.from;
  return Boolean(from && from !== ex.id);
}

/** Texte de la relation : « Ordre reçu de « 11BP » », « 8 dossiers subordonnés » ; vide sans relation. */
export function relationText(relation: { parent: string | null; children: number }): string[] {
  const out: string[] = [];
  if (relation.parent) out.push(t("Ordre reçu de « {name} »", { name: relation.parent }));
  if (relation.children)
    out.push(relation.children === 1 ? t("1 dossier subordonné") : t("{n} dossiers subordonnés", { n: relation.children }));
  return out;
}

/** Parties d'un dossier, dans l'ordre du déroulé d'un exercice. */
export type Part = "mission" | "map" | "briefing" | "order" | "direx" | "aar";

/** Ton d'une partie dont l'état sort de l'ordinaire : `run` (terrain en file ou en préparation), `danger`
 *  (préparation en échec), `muted` (terrain pas encore connu : gris, même désignée comme prochaine) ; sans ton,
 *  faite (vert), prochaine (accent) ou à venir (gris). */
export type PartTone = "run" | "danger" | "muted";

export interface PartState {
  done: boolean;
  status: string;
  tone?: PartTone;
}

/** Terrain d'un dossier pas encore connu : la liste des zones n'est pas lue (ou sa lecture a échoué) et rien ne
 *  dit que le dossier est sur un camp. L'étape « Carte » n'est alors ni faite ni affichée comme telle. */
export const ZONE_UNKNOWN = "unknown";

/** Zone libre d'un dossier pour son avancement : la zone, null (camp, ou zone absente de la liste lue), ou
 *  `ZONE_UNKNOWN` tant qu'on ne le sait pas. */
export type DossierZoneState = Pick<Zone, "status"> | null | undefined | typeof ZONE_UNKNOWN;

/** Étape « Carte » d'un dossier posé sur une zone libre pas prête (docs/14 §2.7.5) : l'état du terrain plutôt que
 *  « Dispositif placé » — en file, en préparation, en échec, annulée, ou en vérification tant que la liste des
 *  zones n'est pas lue ; null pour une zone prête (une extension en cours laisse le terrain servi) ou un camp. */
export function zoneMapState(zone: DossierZoneState): PartState | null {
  // ui-check: ignore-start — libellé d'attente affiché par <Waiting> là où il est rendu (WelcomeScreen, étapes du dossier)
  if (zone === ZONE_UNKNOWN) return { done: false, status: t("Terrain : vérification…"), tone: "muted" };
  // ui-check: ignore-end
  switch (zone?.status) {
    case "queued":
      return { done: false, status: t("Terrain en file"), tone: "run" };
    case "preparing":
      return { done: false, status: t("Terrain en préparation"), tone: "run" };
    case "failed":
      return { done: false, status: t("Préparation en échec"), tone: "danger" };
    case "cancelled":
      return { done: false, status: t("Préparation annulée") };
    default:
      return null;
  }
}

/** Où en est chaque partie d'un dossier (avancement de la carte « Reprendre », pastilles des accès directs) :
 *  faite ou non, et son état en deux ou trois mots. `zone` : la zone libre du dossier (liste des zones de la
 *  licence), dont l'état prend l'étape « Carte » tant qu'elle n'est pas prête ; sans elle (camp), rien ne change ;
 *  `ZONE_UNKNOWN` (liste pas encore lue) : étape en vérification, non comptée. */
export function partStates(ex: Exercise, zone?: DossierZoneState): Record<Part, PartState> {
  const complete = isComplete(ex);
  const briefed = Boolean(ex.progress?.briefing);
  const orders = ex.orders?.count ?? 0;
  const finalized = ex.orders?.finalized ?? 0;
  const incidents = ex.progress?.incidents ?? 0;
  const evaluated = ex.progress?.evaluated ?? 0;
  const ordersStatus =
    orders === 0
      ? t("À rédiger")
      : finalized < orders
        ? t("En rédaction")
        : orders === 1
          ? t("Validé")
          : t("{n} validés", { n: orders });
  return {
    mission: { done: complete, status: complete ? t("Complètes") : t("À compléter") },
    map: complete ? (zoneMapState(zone) ?? { done: true, status: t("Dispositif placé") }) : { done: false, status: t("Après les Données initiales") },
    briefing: { done: briefed, status: briefed ? t("Rédigée") : t("À rédiger") },
    order: { done: orders > 0, status: ordersStatus },
    direx: {
      done: incidents > 0,
      status: incidents === 0 ? t("À préparer") : incidents === 1 ? t("1 incident") : t("{n} incidents", { n: incidents }),
    },
    aar: {
      done: evaluated > 0,
      status: evaluated === 0 ? t("À évaluer") : t("{n} sur {total} notés", { n: evaluated, total: incidents }),
    },
  };
}

/** Zone libre d'un dossier dans la liste des zones de la licence ; undefined pour un camp (ou tant que la liste
 *  n'est pas lue). */
export function dossierZone(zones: readonly Zone[] | undefined, slug: string | null | undefined): Zone | undefined {
  return slug ? zones?.find((z) => z.slug === slug) : undefined;
}

/** Terrain d'un dossier pour son avancement : la zone libre trouvée dans la liste lue ; null pour un camp (connu
 *  par la liste des cartes, ou absent de la liste des zones lue) ou sans terrain ; `ZONE_UNKNOWN` tant que la liste
 *  des zones n'est pas lue (ou que sa lecture a échoué) et que rien ne dit que c'est un camp — l'étape « Carte »
 *  ne passe pas pour faite avant de le savoir. */
export function dossierZoneState(
  slug: string | null | undefined,
  zones: readonly Zone[] | undefined,
  maps: readonly { slug: string; kind?: string }[] | undefined,
): Zone | null | typeof ZONE_UNKNOWN {
  if (!slug) return null;
  // Les zones libres ont un slug « zone-<12 hex>[-nom] » (serveur, docs/14 §3.6) : un camp est reconnu tout de suite,
  // sans attendre la liste des zones (pas de « vérification » fugace sur l'accueil).
  if (!slug.startsWith("zone-")) return null;
  const zone = zones?.find((z) => z.slug === slug);
  if (zone) return zone;
  if (zones) return null;
  const map = maps?.find((m) => m.slug === slug);
  return map && map.kind !== "user" ? null : ZONE_UNKNOWN;
}

/** Pastille d'une étape de l'avancement : faite (vert), terrain en préparation (`run`), en échec (`danger`),
 *  prochaine à faire (accent), à venir (cerclée). */
export function stepCircle(state: PartState, current: boolean): string {
  if (state.done) return "bg-ok text-white";
  if (state.tone === "run") return "bg-run text-white";
  if (state.tone === "danger") return "bg-danger text-white";
  if (state.tone === "muted") return "bg-surface text-ink-muted ring-1 ring-line-strong";
  return current ? "bg-accent text-white" : "bg-surface text-ink-muted ring-1 ring-line-strong";
}

/** Couleur de l'état d'une étape, accordée à sa pastille. */
export function stepText(state: PartState, current: boolean): string {
  if (state.done) return "text-ok";
  if (state.tone === "run") return "text-run";
  if (state.tone === "danger") return "text-danger";
  if (state.tone === "muted") return "text-ink-muted";
  return current ? "text-accent" : "text-ink-muted";
}

/** Mission posée par défaut à la génération des ordres subordonnés (aucune mission du niveau ne correspond à ce que
 *  l'ordre du chef donne, ou l'unité n'y figure pas) et pas encore choisie : à choisir, jamais montrée comme arrêtée. */
export function missionToChoose(ex: Exercise): boolean {
  const declined = ex.parameters.extra?.declination as { mission_open?: unknown; mission?: unknown } | undefined;
  return Boolean(declined?.mission_open) && declined?.mission === ex.parameters.mission;
}

/** Force commandée et mission (« 1erRCP · Prendre le contact »), ou ce qui manque encore. */
export function forceAndMission(ex: Exercise): string {
  // Rotation CENTAC : la mission est la rotation jouée (registre A119), qui nomme aussi le dossier (registre A122) : pas
  // répétée sous le nom « Rotation 08/26 » ; seulement pour un dossier qui garde un autre nom.
  const rotation = rotationName(rotationCode(ex.parameters));
  const mission = ex.parameters.level === "CENTAC" ? (rotation && !ex.name.startsWith(rotation) ? rotation : "") : missionName(ex.parameters.mission);
  const shown = mission && missionToChoose(ex) ? t("{mission} (à choisir)", { mission }) : mission;
  const parts = [unitDesignation(ex.force_label), shown].filter(Boolean);
  if (parts.length) return parts.join(" · ");
  return ex.parameters.level ? levelLabel(ex.parameters.level, ex.level_label) : t("Données initiales à compléter");
}

/** Ennemi joué : faction de la division adverse, ou la FORAD de la rotation CENTAC ; null face à l'ennemi
 *  générique (force non française, registre B39 : aucun nom d'ennemi de référence). */
export function enemyOf(ex: Exercise): string | null {
  if (ex.parameters.level === "CENTAC") return "FORAD";
  if (isNeutral(ex.reference_enemy)) return null;
  // ENI personnalisé (registre A135) : son nom de code, jamais l'ennemi de référence porteur.
  return ex.custom_enemy_code ?? ex.parameters.enemy_type;
}

/** Repère « Ennemi » d'une carte de dossier : « Ennemi TITAN », « Ennemi FORAD », ou « Ennemi générique » pour une
 *  force non française ; null sans ennemi connu. */
export function enemyMeta(ex: Exercise): string | null {
  const base = enemyBaseMeta(ex);
  // Profil du chef ENI (registre D18) : affiché avec l'ennemi, deux exercices du même dossier restent comparables.
  const profile = ex.parameters.level !== "CENTAC" ? ex.parameters.enemy_profile : null;
  if (!base || !profile) return base;
  return t("{enemy} · chef {profile}", { enemy: base, profile: profileName(profile).toLowerCase() });
}

function enemyBaseMeta(ex: Exercise): string | null {
  if (isNeutral(ex.reference_enemy) && ex.parameters.level !== "CENTAC") return genericEnemyLabel();
  const enemy = enemyOf(ex);
  return enemy ? t("Ennemi {enemy}", { enemy }) : null;
}

/** Texte cherché : nom, camp, force, mission, niveau. */
export function searchText(ex: Exercise): string {
  return fold(
    // Mission et niveau dans les deux langues : on cherche avec les mots de l'interface affichée.
    [ex.name, ex.map_name, ex.force_label, rotationName(rotationCode(ex.parameters)), missionLabel(ex.parameters.mission), missionName(ex.parameters.mission), levelLabel(ex.parameters.level, ex.level_label), ex.parameters.level]
      .filter(Boolean)
      .join(" "),
  );
}

/** Import d'un fichier « .generex » : le dossier créé, ou une erreur lisible. */
export async function importDossierFile(file: File, queryClient: QueryClient): Promise<Exercise> {
  let payload: unknown;
  try {
    payload = JSON.parse(await file.text());
  } catch {
    throw new Error(t("Fichier illisible : choisissez un dossier « .generex »."));
  }
  try {
    const ex = await api.importExercise(payload);
    await queryClient.invalidateQueries({ queryKey: ["exercises"] });
    return ex;
  } catch (err) {
    throw new Error(err instanceof ApiError ? err.message : t("Import impossible : le serveur ne répond pas."));
  }
}
